import {
  PendingSourceDocument,
  usePendingSourceUpdateMutation,
} from '@mnd-apps/publish/generated/hooks';
import type { PendingSourceFragment } from '@mnd-apps/publish/generated/types';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { media } from '@mnd-frontend/ui/publish/utils';
import { SPACING_1 } from '@mnd-frontend/ui/shared/tokens';
import { differenceWith, isEqual } from 'lodash-es';
import {
  type Dispatch,
  lazy,
  type MutableRefObject,
  type SetStateAction,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import CardCompetitor, { CARD_WIDTH, STATUS } from '../components/CardCompetitor';
import { useOnboardingLocaleText } from '../hooks/useOnboardingLocaleText';
import { STEP } from '../types';

const OnboardingModalCompetitor = lazy(() => import('../components/OnboardingModal'));

const CompetitorsGrid = styled.ul<{ $cols: number; $singleRow: boolean }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 25rem;
  margin: 0 auto;
  padding: 0;
  list-style: none;
  gap: ${SPACING_1};
  overflow-y: auto;
  height: 15rem;
  scrollbar-width: thin;

  ${media.greaterThan('desktop')} {
    ${({ $cols, $singleRow }) => {
      if ($singleRow) {
        return css`
          max-width: calc(${CARD_WIDTH} * ${$cols} + ${SPACING_1} * (${$cols} - 1));
          min-height: ${CARD_WIDTH};
        `;
      }

      return css`
        max-width: calc(${CARD_WIDTH} * ${$cols} + ${SPACING_1} * (${$cols} - 1));
        height: 18rem;
      `;
    }}
  }
`;

type StepCompetitorsProps = {
  originalPendingSource: PendingSourceFragment;
  selectedBrandIds: string[];
  setSelectedBrandIds: Dispatch<SetStateAction<string[]>>;
  trackCompetitorChange: MutableRefObject<Set<string>>;
};

const StepCompetitors = ({
  originalPendingSource,
  selectedBrandIds,
  setSelectedBrandIds,
  trackCompetitorChange,
}: StepCompetitorsProps) => {
  const { t } = useTranslation();
  const { showFailureToast } = useToasts();

  const [brandName, setBrandName] = useState('');
  const [brandUrl, setBrandUrl] = useState('');
  const previousCompetitorsData = useRef(originalPendingSource.competitors);

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setBrandName('');
    setBrandUrl('');
  };

  // User adds competitors manually.
  const [updatePendingSource, { loading, data, reset }] = usePendingSourceUpdateMutation({
    refetchQueries: [PendingSourceDocument, 'PendingSource'],
    onCompleted: async updatedData => {
      const error = updatedData.pendingSourceUpdate?.error;
      if (error) {
        if (error.__typename !== 'PendingSourceUpdateValidationError') {
          showFailureToast(error.message);
        }
      } else {
        const newListOfCompetitors =
          updatedData.pendingSourceUpdate?.pendingSource?.competitors ?? [];

        const diff = differenceWith(newListOfCompetitors, previousCompetitorsData.current, isEqual);

        const newCompetitor = diff.length ? diff[0] : null;

        if (newCompetitor) {
          trackCompetitorChange.current.add(newCompetitor.id);
          setSelectedBrandIds(prev => [...prev, newCompetitor.id]);
        }

        previousCompetitorsData.current = newListOfCompetitors;

        handleCloseModal();
      }
    },
    onError: () => showFailureToast(t('onboarding_trial_error_update')),
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const cardStatus = useCallback(
    (id: string) => {
      if (selectedBrandIds.includes(id)) {
        return STATUS.SELECTED;
      }
      if (selectedBrandIds.length > 1) {
        return STATUS.DISABLED;
      }

      return STATUS.DEFAULT;
    },
    [selectedBrandIds],
  );

  const handleSelect = (id: string) => {
    let updateSelection = [...selectedBrandIds];

    if (cardStatus(id) === STATUS.DEFAULT) {
      updateSelection.push(id);
      setSelectedBrandIds(prev => [...prev, id]);
    } else {
      const unselectedIds = selectedBrandIds.filter(selectedId => selectedId !== id);
      updateSelection = unselectedIds;
      setSelectedBrandIds(unselectedIds);
    }
  };

  const localeText = useOnboardingLocaleText()[STEP.COMPETITORS];

  /**
   * UI grid criteria:
   * @ref https://mynewsdesk.atlassian.net/browse/GROW-352
   */
  const gridCols = useMemo(() => {
    const numOfItems = originalPendingSource.competitors.length;
    if (numOfItems === 3 || (numOfItems >= 6 && numOfItems < 8)) return 4;
    if (numOfItems < 6) return 3;
    return 5;
  }, [originalPendingSource.competitors.length]);

  return (
    <>
      <CompetitorsGrid $cols={gridCols} $singleRow={originalPendingSource.competitors.length < 4}>
        {originalPendingSource.competitors.length > 0 &&
          originalPendingSource.competitors.map(competitor => (
            <li key={competitor.id}>
              <CardCompetitor
                status={cardStatus(competitor.id)}
                name={competitor.brandName}
                logoSrc={competitor.logoUrl}
                onClick={() => handleSelect(competitor.id)}
              />
            </li>
          ))}
        <li>
          {selectedBrandIds.length > 1 ? (
            <CardCompetitor status={STATUS.EMPTY_DISABLED} />
          ) : (
            <CardCompetitor
              status={STATUS.EMPTY}
              onClick={() => {
                setIsEditModalOpen(true);
              }}
            />
          )}
        </li>
        {originalPendingSource.competitors.length === 0 && (
          <li>
            <CardCompetitor
              status={STATUS.EMPTY}
              onClick={() => {
                setIsEditModalOpen(true);
              }}
            />
          </li>
        )}
      </CompetitorsGrid>
      <Suspense>
        <OnboardingModalCompetitor
          type={STEP.COMPETITORS}
          currentStepLocaleText={localeText}
          pendingSourceId={originalPendingSource.id}
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
          onUpdateMutation={updatePendingSource}
          onUpdateReset={reset}
          updating={loading}
          updatedSource={data?.pendingSourceUpdate}
          fieldController={{ brandName, setBrandName, brandUrl, setBrandUrl }}
        />
      </Suspense>
    </>
  );
};

export default StepCompetitors;
